import { ref, computed, watch } from 'vue'
import { api } from '@/lib/api'
import { useAuth } from './useAuth'
import { formatMoney } from '@/lib/utils/money'
import { formatDate } from '@/lib/utils/date'

interface Transaction {
  id: string
  amount: number
  roundup: number
  timestamp: string
  currency: string
  roundupApplied: boolean | null
  meta?: {
    counter_party_preferred_name?: string
  }
  description?: string
}

interface Account {
  id: string
  account_number: {
    iban: string
  }
  display_name: string
  roundups: boolean
  transactions: Transaction[]
}

interface FormattedTransaction extends Transaction {
  date: string
  amountFormatted: string
  roundupFormatted: string
}

interface LinkedBankAccount {
  id: string
  balance: string
  accountNumber: string
  bank: string
  roundupsEnabled: boolean
  transactions: FormattedTransaction[]
}

interface Graph {
  labels: string[]
  datasets: {
    data: number[]
    color?: (opacity: number) => string
    strokeWidth?: number
  }[]
}

const { user } = useAuth()
const accounts = ref<Account[]>([])
const graph = ref<Graph>({
  labels: [],
  datasets: [
    {
      data: [],
    },
  ],
})

const linkedBankAccounts = computed<LinkedBankAccount[]>(() =>
  (accounts.value || []).map((account) => ({
    id: account.id,
    balance: formatMoney(
      account.transactions.reduce(
        (acc, transaction) => acc + transaction.roundup,
        0,
      ) / 100,
    ),
    accountNumber: account.account_number.iban,
    bank: account.display_name,
    roundupsEnabled: account.roundups,
    transactions: account.transactions.map((transaction) => ({
      ...transaction,
      date: formatDate(transaction.timestamp) || '',
      amountFormatted: formatMoney(-transaction.amount, {
        currency: transaction.currency,
        stripZeroCents: false,
      }),
      roundupFormatted: formatMoney(transaction.roundup / 100, {
        stripZeroCents: false,
      }),
    })),
  })),
)

async function refetchAccounts() {
  if (!user.value) {
    accounts.value = []
    return
  }
  try {
    const response = await api.get<Account[]>('/accounts')
    accounts.value = response.data
  } catch (error) {
    console.error('Failed to fetch accounts:', error)
  }
}

async function updateAccount(id: string, data: Partial<Account>) {
  if (!user.value) return

  try {
    const response = await api.patch<Account>(`/accounts/${id}`, data)
    accounts.value = accounts.value.map((account) =>
      account.id === id ? response.data : account,
    )
  } catch (error) {
    console.error('Failed to update account:', error)
    throw error
  }
}

async function applyRoundups() {
  if (!user.value) return

  try {
    await api.post('/roundups/apply')
    await refetchAccounts()
    await useAuth().refreshProfile()
  } catch (error) {
    console.error('Failed to apply roundups:', error)
    throw error
  }
}

async function clearRoundups() {
  if (!user.value) return

  try {
    await api.post('/roundups/clear')
    await refetchAccounts()
  } catch (error) {
    console.error('Failed to clear roundups:', error)
    throw error
  }
}

async function fetchGraph() {
  if (!user.value) return

  try {
    const response = await api.get<Graph>('/graph')
    response.data.datasets[0] = {
      ...response.data.datasets[0],
      color: (opacity = 1) => `rgba(82, 97, 172, ${opacity})`,
      strokeWidth: 3,
    }
    graph.value = response.data
  } catch (error) {
    console.error('Failed to fetch graph:', error)
  }
}

watch(user, (user) => {
  if (user) {
    refetchAccounts()
    fetchGraph()
  }
})

export function useAccounts() {
  return {
    accounts: linkedBankAccounts,
    refetchAccounts,
    updateAccount,
    applyRoundups,
    clearRoundups,
    graph,
  }
}
